

/**
   * @component Range
   *
   * Accessible slider that allows to define value between
   * min and max value.
   *
   * @prop {Number?} value
   * @prop {Number?} min
   * @prop {Number?} max
   * @prop {Function?} @change
   * @public
   */

export default {
  name: 'Range',
  props: {
    min: {
      type: Number,
      default: 0,
    },
    max: {
      type: Number,
      default: 100,
    },
    value: {
      type: Number,
      default: 0,
    },
    label: {
      type: String,
      default: '',
    },
    ratio: {
      type: Number,
      default: 1,
    },
  },
  data() {
    return {
      width: 0,
    };
  },

  computed: {

    /**
       * Generate styles to move cursor down the slider rail.
       *
       * @private
       */

    style() {
      const {
        value, width, min, max, ratio,
      } = this;
      return {
        left: `${Math.round((value * width) / ((max / ratio) - (min / ratio))) - 16}px`,
      };
    },
  },

  /**
     * Get slider width once component is mounted and initialize
     * cursor position.
     *
     * @private
     */

  mounted() {
    this.width = this.$refs.rail.getBoundingClientRect().width;
  },
  methods: {

    /**
       * Listen for cursor movements.
       *
       * @private
       */

    listen() {
      this.width = this.$refs.rail.getBoundingClientRect().width;
      const move = this.handleMove.bind(this);

      function up() {
        document.removeEventListener('pointermove', move);
        document.removeEventListener('pointerup', up);
      }

      document.addEventListener('pointermove', move);
      document.addEventListener('pointerup', up);
      this.$refs.cursor.focus();
    },

    keydown(event) {
      const { value, min, max } = this;
      const step = increment(event.keyCode, min, max);
      this.move(value + step);
      if (step != null) {
        event.preventDefault();
        event.stopPropagation();
      }
    },

    /**
       * Focus slider.
       *
       * @private
       */

    focus() {
      this.$refs.rail.classList.add('focus');
    },

    /**
       * Blur slider.
       *
       * @private
       */

    blur() {
      this.$refs.rail.classList.remove('focus');
    },

    /**
       * Move slider thumb to the given position.
       *
       * @param {Number} valueInput
       * @public
       */

    move(valueInput) {
      let value = valueInput;
      const { min, max, ratio } = this;
      if (value > max) value = max;
      else if (value < min) value = min;
      this.$emit('change', value / ratio);
    },

    /**
       * Click rail, set cursor position and focus
       * slider.
       *
       * @param {Object} event
       * @private
       */

    clickRail(event) {
      this.handleMove(event);
      this.$refs.cursor.focus();
    },

    /**
       * Move cursor based on mouse position.
       *
       * @param {Object} event
       * @private
       */

    handleMove(event) {
      const { min, max, width } = this;
      const diffX = event.pageX - this.$refs.rail.offsetLeft;
      this.move(parseInt(((max - min) * diffX) / width, 10));
      event.preventDefault();
      event.stopPropagation();
    },

  },
};

/**
   * Map key code with label.
   * @type {Object}
   */

const keyCode = {
  left: 37,
  up: 38,
  right: 39,
  down: 40,
  pageUp: 33,
  pageDown: 34,
  end: 35,
  home: 36,
};

/**
   * Returns cursor increment based on keydown code.
   *
   * @param {Number} code
   * @param {Number} min
   * @param {Number} max
   * @return {Number}
   * @private
   */

function increment(code, min, max) {
  switch (code) {
    case keyCode.left:
    case keyCode.down:
      return -1;
    case keyCode.right:
    case keyCode.up:
      return 1;
    case keyCode.pageDown:
      return -10;
    case keyCode.pageUp:
      return 10;
    case keyCode.home:
      return min;
    case keyCode.end:
      return max;
    default:
      return 0;
  }
}

